import { Close, Download, ExpandMore, Preview } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	IconButton,
	Modal,
	Paper,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import type React from "react";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { FileItemDto } from "../../../api/rest/generated/aPIForPublicPartOfApplicationInzerceNemovitéhoMajetku.schemas.ts";
import {
	useGetApiAssetsmultimediaAdvertisementIdAttachmentsSuspense,
	useGetApiAssetsmultimediaAttachmentMultimediaId,
} from "../../../api/rest/generated/assets-multimedia/assets-multimedia.ts";
import DocumentTypeIcon from "../../icons/react icons/sz-icons/DocumentTypeIcon.tsx";
import CircularProgressWithLabel from "../UISharedConstructors/CircularProgressWithLabel.tsx";
import SzBackdrop from "../UISharedConstructors/Loading/SzBackDrop.tsx";
import { isEvyveska } from "../../../utills/iframeVariants.ts";

const AttachmentsFallBack: React.FC = () => {
	return (
		<>
			<Skeleton variant="rounded" />
			<Skeleton variant="rounded" />
			<Skeleton variant="rounded" />
			<Skeleton variant="rounded" />
		</>
	);
};

const AttachmentItem: React.FC<{ fileItem: FileItemDto }> = ({ fileItem }) => {
	const [open, setOpen] = useState(false);
	const [progress, setProgress] = useState(0);
	const attachmentQuery = useGetApiAssetsmultimediaAttachmentMultimediaId(fileItem?.id ?? -1, {
		query: {
			enabled: false,
		},
		request: {
			onProgress: (progress) => {
				setProgress(progress);
			},
		},
	});

	const handleDownload = () => {
		if (attachmentQuery.data) {
			const blobUrl = URL.createObjectURL(attachmentQuery.data);
			const link = document.createElement("a");

			link.href = blobUrl;
			link.download = fileItem.fileName ?? "neznámý soubor";
			// Append link to the body
			document.body.appendChild(link);

			// Dispatch click event on the link
			// This is necessary as link.click() does not work on the latest firefox
			link.dispatchEvent(
				new MouseEvent("click", {
					bubbles: true,
					cancelable: true,
					view: window,
				}),
			);

			// Remove link from body
			document.body.removeChild(link);
		} else {
			attachmentQuery
				.refetch()
				.then((res) => {
					const blob = res.data;
					if (blob) {
						const blobUrl = URL.createObjectURL(blob);
						const link = document.createElement("a");

						link.href = blobUrl;
						link.download = fileItem.fileName ?? "neznámý soubor";
						// Append link to the body
						document.body.appendChild(link);

						// Dispatch click event on the link
						// This is necessary as link.click() does not work on the latest firefox
						link.dispatchEvent(
							new MouseEvent("click", {
								bubbles: true,
								cancelable: true,
								view: window,
							}),
						);

						// Remove link from body
						document.body.removeChild(link);
					}
				})
				.catch((reason) => console.error(reason));
		}
	};

	const handlePreview = () => {
		if (!attachmentQuery.data) {
			attachmentQuery.refetch().catch((reason) => console.error(reason));
		}
		setOpen(true);
	};

	return (
		<Paper elevation={0}>
			<Stack
				sx={(theme) => ({
					"&:hover": {
						backgroundColor: theme.palette.grey[200],
					},
				})}
				width="100%"
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				spacing={2}
			>
				<Stack width="100%" direction="row" alignItems="center" spacing={2}>
					<DocumentTypeIcon documentType={fileItem?.extension ? fileItem.extension.substring(1) : undefined} />
					<Typography>Název souboru: {fileItem.fileName ?? "bez názvu"}</Typography>
					<Typography sx={{ whiteSpace: "pre-wrap" }}>Popis: {fileItem.description ?? "bez popisu"}</Typography>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={2}>
					{fileItem?.extension === ".pdf" && (
						<IconButton onClick={handlePreview}>
							<Preview />
						</IconButton>
					)}
					<IconButton
						loadingIndicator={<CircularProgressWithLabel variant="determinate" value={progress} />}
						loading={attachmentQuery.isLoading}
						onClick={handleDownload}
					>
						<Download />
					</IconButton>
				</Stack>
			</Stack>
			<Modal
				open={open}
				onClose={() => setOpen(false)}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box height="100%" bottom={0} left="50%">
					<Stack
						direction="row"
						sx={{
							justifyContent: "space-between",
							alignItems: "center",
							backgroundColor: "background.paper",
						}}
					>
						<Typography
							variant="h6"
							sx={{
								paddingLeft: "1rem",
							}}
						>
							Náhled PDF
						</Typography>
						<Tooltip placement="top" arrow={true} title="zavřít náhled">
							<IconButton onClick={() => setOpen(false)}>
								<Close />
							</IconButton>
						</Tooltip>
					</Stack>
					<Box
						sx={{
							height: "100%",
							backgroundColor: "background.paper",
						}}
					>
						{attachmentQuery.isLoading && (
							<Box height="100%" width="100%">
								<SzBackdrop message={`načítám náhled ${fileItem.fileName}`} />
							</Box>
						)}
						{attachmentQuery.data &&
							// Create the preview URL before returning the JSX
							(() => {
								const previewUrl = URL.createObjectURL(attachmentQuery.data);
								return <iframe width="100%" height="100%" src={previewUrl} title="PDF Preview" />;
							})()}
					</Box>
				</Box>
			</Modal>
		</Paper>
	);
};

const Attachments: React.FC<{ id: number }> = ({ id }) => {
	const attachmentsQuery = useGetApiAssetsmultimediaAdvertisementIdAttachmentsSuspense(id, {
		query: {
			retry: false,
		},
	});

	const data = attachmentsQuery.data;

	if (data.length === 0) {
		return <Typography>Tento inzerát je bez příloh.</Typography>;
	}

	return (
		<Stack width="100%" spacing={2}>
			{data
				.sort((a, b) => (a?.order !== undefined && b?.order !== undefined ? a.order - b.order : data.length - 1))
				.map((item) => (
					<AttachmentItem key={item.id} fileItem={item} />
				))}
		</Stack>
	);
};

const AttachmentsSection: React.FC<{ id: number }> = ({ id }) => {
	const [expandedAttachments, setExpandedAttachments] = useState<boolean>(true);

  if(isEvyveska()) {
    return null
  }

  return (
		<Accordion expanded={expandedAttachments}>
			<Tooltip followCursor={true} placement="right" title={expandedAttachments ? "Skrýt přílohy" : "Zobrazit přílohy"}>
				<AccordionSummary
					onClick={() => setExpandedAttachments(!expandedAttachments)}
					sx={{ backgroundColor: "grey.200" }}
					expandIcon={<ExpandMore fontSize="medium" color="secondary" />}
				>
					<Typography fontWeight="bolder" sx={{ color: "info.main" }} variant="subtitle1" component="div">
						Přílohy
					</Typography>
				</AccordionSummary>
			</Tooltip>
			<AccordionDetails>
				<ErrorBoundary
					fallbackRender={() => <Typography>Tento inzerát je bez příloh.</Typography>}
				>
					<Suspense fallback={<AttachmentsFallBack />}>
						<Attachments id={id} />
					</Suspense>
				</ErrorBoundary>
			</AccordionDetails>
		</Accordion>
	);
};

export default AttachmentsSection;
